import { RouteProp } from "@react-navigation/native";
import React, { useState } from "react";
import { ActivityIndicator, Platform } from "react-native";
import WebView from "react-native-webview";
import { config } from "../../config";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorParamList,
} from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import { FileScreenQuery } from "./__generated__/FileScreenQuery.graphql";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { useNavigationHeader } from "../../common/navigationHeaderOptions";
import { useIntl } from "react-intl";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { useAlert } from "../../common/Alert";
import { FileScreenDeleteMutation } from "./__generated__/FileScreenDeleteMutation.graphql";
import { Box } from "arkh";
import { useSafeAreaInsets } from "react-native-safe-area-context";

export const FileScreen = withWebModalStack(FileScreenC, true, {
  width: 800,
  height: 900,
});
function FileScreenC(props: {
  route: RouteProp<UserLoggedInNavigatorParamList, "File">;
  navigation: UserLoggedInNavigatorNavigationProp<"File">;
}) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => <File {...props} fetchKey={fk} />}
    </LazyQueryNetworkBoundrary>
  );
}
function File(props: {
  route: RouteProp<UserLoggedInNavigatorParamList, "File">;
  navigation: UserLoggedInNavigatorNavigationProp<"File">;
  fetchKey: string;
}) {
  const [loading, setLoading] = useState(false);

  const { file } = useLazyLoadQuery<FileScreenQuery>(
    graphql`
      query FileScreenQuery($id: ID!) {
        file(id: $id) {
          name
          pdfUrl
          hasWriteAccess
        }
      }
    `,
    {
      id: props.route.params.id,
    },
    {
      fetchPolicy: "store-and-network",
      fetchKey: props.fetchKey,
    }
  );

  const intl = useIntl();
  const intl_tokens = useIntlTokens();

  const Alert = useAlert();
  const [deleteMutation] = useMutation<FileScreenDeleteMutation>(graphql`
    mutation FileScreenDeleteMutation($id: ID!) {
      file(id: $id) {
        delete
      }
    }
  `);
  const onRequestDelete = async () => {
    Alert.alert(
      intl.formatMessage({
        defaultMessage: "Delete file",
        description: "FileInfoScreen Delete file alert title",
      }),
      intl.formatMessage({
        defaultMessage: "Are you sure you want to delete the file?",
        description: "FileInfoScreen Delete file alert subtitle",
      }),
      [
        {
          text: intl_tokens.Delete,
          style: "destructive",

          onPress: async () => {
            await deleteMutation({
              variables: {
                id: props.route.params.id,
              },
              updater: (store) => {
                store.delete(props.route.params.id);
              },
            });

            props.navigation.goBack();
          },
        },
        { style: "cancel", onPress: () => {}, text: intl_tokens.Cancel },
      ]
    );
  };
  const insets = useSafeAreaInsets();
  useNavigationHeader({
    type: "CLOSE",
    onClose: props.navigation.goBack,
    title: file?.name ?? "",
    more: file?.hasWriteAccess
      ? [
          {
            label: intl_tokens.More,
            icon: "info",
            onPress: () => {
              props.navigation.navigateDeprecated("FileInfo", {
                id: props.route.params.id,
              });
            },
          },
          {
            type: "button",
            label: intl_tokens.Delete,
            icon: "trash",
            onPress: onRequestDelete,
          },
        ]
      : undefined,
  });
  if (!file?.pdfUrl) {
    return <Box color="surface-primary" style={{ flex: 1 }}></Box>;
  }
  if (Platform.OS === "web") {
    return <iframe style={{ height: "100%" }} src={file?.pdfUrl} />;
  }
  const fileName = (file?.name ?? "") || "untitled";
  return (
    <Box color="surface-primary" style={{ flex: 1 }}>
      <WebView
        load
        style={{ flex: 1 }}
        onLoadStart={() => setLoading(true)}
        onLoadEnd={() => setLoading(false)}
        onError={(e) => {
          console.log(e);
        }}
        decelerationRate={Platform.OS === "ios" ? "normal" : undefined}
        containerStyle={{
          paddingBottom: Platform.OS === "ios" ? insets.bottom : 0,
        }}
        source={
          Platform.OS === "android"
            ? {
                baseUrl: config.adobePDFPreviewBaseURL,
                html: `
          <html>
          <head>
          <script src="https://documentservices.adobe.com/view-sdk/viewer.js"></script>
        </head>
        <body>
        <div id="adobe-dc-view"></div>
        <script type="text/javascript">
        document.addEventListener("adobe_dc_view_sdk.ready", function()
        {
          var adobeDCView = new AdobeDC.View({clientId: "${config.adobePDFPreviewClientId}", divId: "adobe-dc-view"});
          adobeDCView.previewFile(
            {
              content:  {location: {url: ${JSON.stringify(file.pdfUrl)}}},
              metaData: {fileName: ${JSON.stringify(fileName)}}
          });
        });
        </script>
        
        </body>
        </html>
        `,
                uri: file.pdfUrl,
              }
            : { uri: file.pdfUrl }
        }
      />
      {loading ? (
        <ActivityIndicator
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            flexWrap: "wrap",
            alignContent: "center",
          }}
          size="small"
        />
      ) : null}
    </Box>
  );
}
